import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Loader } from '@lms/ui';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { api } from '@/lib/api';
import { activityPercentage } from '@/lib/grade-percentage';

interface StudentCourseGradeItem {
  id: string;
  title: string;
  score: number | null;
  maxScore: number;
  percentage: number | null;
  status: string;
  gradedAt: string | null;
}

interface StudentCourseGradesData {
  course: { id: string; name: string };
  courseAverage: number | null;
  items: StudentCourseGradeItem[];
}

function formatScore(value: number): string {
  return value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusLabel(status: string): string {
  switch (status) {
    case 'graded':
      return 'Calificada';
    case 'passed':
      return 'Aprobada';
    case 'failed':
      return 'Reprobada';
    case 'pending':
      return 'Pendiente';
    default:
      return status;
  }
}

function statusVariant(status: string): 'success' | 'danger' | 'secondary' | 'primary' {
  if (status === 'passed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'graded') return 'primary';
  return 'secondary';
}

export function StudentCourseGradesPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['student', 'courses', id, 'grades'],
    queryFn: () => api<StudentCourseGradesData>(`/student/courses/${id}/grades`),
    enabled: Boolean(id),
  });

  if (!id) return null;

  const items = data?.items ?? [];

  return (
    <CourseHubShell
      courseId={id}
      courseName={data?.course.name ?? 'Curso'}
      activeTab="calificaciones"
      description="Consulta tus resultados y el avance de evaluaciones en este curso."
      studentHub
    >
      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            <div className="lms-course-forum-card__head">
              <div className="lms-course-forum-card__heading">
                <h2 className="lms-course-forum-card__title">Mis calificaciones</h2>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      grade
                    </span>
                  </div>
                  <h3>Sin calificaciones todavía</h3>
                  <p>Cuando completes evaluaciones en este curso, tus notas aparecerán aquí.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          Actividad
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Estado
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Nota
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Porcentaje
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const percentage = activityPercentage(item);
                        return (
                          <tr key={item.id}>
                            <th className="lms-gradebook__td" scope="row">
                              <span className="lms-gradebook__name">{item.title}</span>
                            </th>
                            <td className="lms-gradebook__td">
                              <Badge variant={statusVariant(item.status)}>{statusLabel(item.status)}</Badge>
                            </td>
                            <td className="lms-gradebook__td">
                              {item.score != null ? (
                                <span className="lms-gradebook__emphasis">
                                  {formatScore(item.score)} / {formatScore(item.maxScore)}
                                </span>
                              ) : (
                                <span className="lms-gradebook__muted">—</span>
                              )}
                            </td>
                            <td className="lms-gradebook__td">
                              {percentage != null ? (
                                <span className="lms-gradebook__emphasis">
                                  {item.status === 'passed' ? (
                                    <span className="material-symbols-outlined lms-gradebook__pass" aria-label="Aprobado">
                                      check
                                    </span>
                                  ) : null}
                                  {formatScore(percentage)}
                                </span>
                              ) : (
                                <span className="lms-gradebook__muted">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">Promedio general</span>
                  <span className="lms-gradebook__emphasis">
                    {data?.courseAverage != null ? formatScore(data.courseAverage) : '—'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </CourseHubShell>
  );
}
